/**
 * Auth Slice for GEM Frontend
 * Redux state management for authentication.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthState, SetUserPayload, SetAuthErrorPayload } from '@/types/auth.types';

export const SESSION_CONFIG = {
  TIMEOUT_MS: 60 * 60 * 1000,
  WARNING_MS: 5 * 60 * 1000,
} as const;

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  lastActivity: null,
  sessionExpiresAt: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<SetUserPayload>) => {
      const { id, email, displayName, persona, roles } = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.user = { id, email, displayName, persona, roles: [...roles] };
      state.error = null;
      state.lastActivity = Date.now();
      state.sessionExpiresAt = Date.now() + SESSION_CONFIG.TIMEOUT_MS;
    },

    clearUser: (state) => {
      state.isAuthenticated = false;
      state.isLoading = false;
      state.user = null;
      state.error = null;
      state.lastActivity = null;
      state.sessionExpiresAt = null;
    },

    setAuthError: (state, action: PayloadAction<SetAuthErrorPayload>) => {
      state.isLoading = false;
      state.error = {
        code: action.payload.code,
        message: action.payload.message,
        timestamp: Date.now(),
      };
    },

    clearAuthError: (state) => {
      state.error = null;
    },

    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
      state.sessionExpiresAt = Date.now() + SESSION_CONFIG.TIMEOUT_MS;
    },

    extendSession: (state) => {
      state.lastActivity = Date.now();
      state.sessionExpiresAt = Date.now() + SESSION_CONFIG.TIMEOUT_MS;
    },
  },
});

export const {
  setUser,
  clearUser,
  setAuthError,
  clearAuthError,
  setAuthLoading,
  updateLastActivity,
  extendSession,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
export default authReducer;

export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectSessionExpiresAt = (state: { auth: AuthState }) => state.auth.sessionExpiresAt;
export const selectLastActivity = (state: { auth: AuthState }) => state.auth.lastActivity;
